import { BonusClassification, Currency, getBonusClassificationTranslationId } from '@synergy/models';
import { Box, Stack, Tooltip } from '@mui/material';
import { CommentNoteIcon } from '@synergy/theme-next';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { PaloAmount, PaloColumn, PaloListView } from '@synergy/components-palo';
import { PoolFormattedMessage, PoolTypography } from '@synergy/components-next';
import { ReactElement } from 'react';
import { ValueOrNDash } from '@synergy/components';

import {
    BonusCondition,
    BonusConditionMode,
    BonusSettlementCustomerDetail,
} from '../models/bonus-settlement-customer-detail';
import { BonusSettlementCustomerDetailBonusConditionTooltip } from './bonus-settlement-customer-detail-bonusCondition-tooltip';

/** One line of the settlement: a supplier and one of its bonus conditions. */
interface SettlementLine {
    key: number;
    supplier: BonusSettlementCustomerDetail;
    condition: BonusCondition;
}

export interface BonusSettlementDetailTableProps {
    currency: Currency;
    requesting?: boolean;
    items: BonusSettlementCustomerDetail[];
}

export const BonusSettlementDetailTable = ({
    currency,
    requesting,
    items,
}: BonusSettlementDetailTableProps): ReactElement => {
    // The figure alone; the currency stands in the column heading (LST-S17).
    const money = (value: number) => <PaloAmount value={value} />;

    const lines: SettlementLine[] = items.flatMap(supplier =>
        (supplier.bonusConditions ?? []).map((condition, index) => ({
            key: supplier.id * 1000000 + index,
            supplier,
            condition,
        }))
    );

    const columns: PaloColumn<SettlementLine>[] = [
        {
            key: 'supplier',
            type: 'name',
            head: <FormattedMessage id="app.bonusSettlements.customerDetail.table.supplier" />,
            cell: ({ supplier }) => (
                <Stack>
                    {supplier.supplierName}
                    <PoolTypography typography="body2" component="span" color="grey.800">
                        {supplier.supplierId}・{supplier.supplierCity}
                    </PoolTypography>
                </Stack>
            ),
        },
        {
            key: 'bonusClassification',
            type: 'classification',
            head: <FormattedMessage id="app.table.column.bonusClassification" />,
            // A bonus plus reads as a contract to the customer.
            cell: ({ supplier }) => {
                const classification =
                    BonusClassification.BonusPlus === supplier.bonusClassification
                        ? BonusClassification.Contract
                        : supplier.bonusClassification;

                return (
                    <ValueOrNDash>
                        {classification && (
                            <FormattedMessage id={getBonusClassificationTranslationId(classification)} />
                        )}
                    </ValueOrNDash>
                );
            },
        },
        {
            key: 'information',
            type: 'icon',
            head: '',
            cell: ({ condition }) => (
                <BonusSettlementCustomerDetailBonusConditionTooltip mode={condition.mode} title={condition.title} />
            ),
        },
        {
            key: 'modePercentage',
            type: 'percent',
            head: <PoolFormattedMessage id="app.bonusSettlements.customerDetail.table.mode.percentage" />,
            cell: ({ condition }) => money(BonusConditionMode.Percentage === condition.mode ? condition.value : 0),
        },
        {
            key: 'modeAmount',
            currency,
            type: 'amount',
            head: <PoolFormattedMessage id="app.bonusSettlements.customerDetail.table.mode.amount" />,
            cell: ({ condition }) => (
                <FormattedNumber
                    style="decimal"
                    minimumFractionDigits={2}
                    maximumFractionDigits={2}
                    value={BonusConditionMode.Amount === condition.mode ? condition.value : 0}
                />
            ),
        },
        {
            key: 'calculatedBonus',
            currency,
            type: 'amount',
            head: <PoolFormattedMessage id="app.bonusSettlements.customerDetail.table.calculatedBonus" />,
            cell: ({ condition }) => money(condition.calculatedBonus),
        },
        {
            key: 'actualBonus',
            currency,
            type: 'amount',
            head: <PoolFormattedMessage id="app.bonusSettlements.customerDetail.table.actualBonus" />,
            cell: ({ supplier, condition }) => (
                <Stack flexDirection="row" justifyContent="right" alignItems="center" gap={1}>
                    {supplier.noteToCustomer && (
                        <Tooltip title={supplier.noteToCustomer} placement="top">
                            <Box sx={{ display: 'flex' }} justifyContent="center">
                                <CommentNoteIcon
                                    sx={{ color: theme => theme.palette.common.black, width: 20, height: 20 }}
                                />
                            </Box>
                        </Tooltip>
                    )}
                    {money(condition.actualBonus)}
                </Stack>
            ),
        },
        {
            key: 'memberBonus',
            currency,
            type: 'amount',
            head: <PoolFormattedMessage id="app.bonusSettlements.customerDetail.table.memberBonus" />,
            cell: ({ condition }) => <strong>{money(condition.memberBonus)}</strong>,
        },
    ];

    return (
        <PaloListView<SettlementLine, number>
            embedded={true}
            columns={columns}
            items={lines}
            getRowId={line => line.key}
            requesting={requesting}
            emptyLabel={<FormattedMessage id="app.common.emptyResult" />}
        />
    );
};
